import { useState, useEffect } from 'react'
import {
  Spinner, Alert, Form, Button, Card, Table, Badge, Modal, ButtonGroup
} from 'react-bootstrap'
import client from '../api/client'

function Memory() {
  const [memories, setMemories] = useState([])
  const [filteredMemories, setFilteredMemories] = useState([])
  const [activeCategory, setActiveCategory] = useState('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingMemory, setEditingMemory] = useState(null)
  const [formData, setFormData] = useState({ key: '', value: '', category: 'fact' })

  useEffect(() => {
    fetchMemories()
  }, [])

  useEffect(() => {
    if (activeCategory === 'all') {
      setFilteredMemories(memories)
    } else {
      setFilteredMemories(memories.filter(m => m.category === activeCategory))
    }
  }, [activeCategory, memories])

  const fetchMemories = async () => {
    setLoading(true)
    try {
      const response = await client.get('/api/v1/memory')
      setMemories(response.data.memories || [])
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingMemory(null)
    setFormData({ key: '', value: '', category: 'fact' })
    setShowModal(true)
  }

  const handleEdit = (memory) => {
    setEditingMemory(memory)
    setFormData({
      key: memory.key,
      value: memory.value,
      category: memory.category
    })
    setShowModal(true)
  }

  const handleDelete = async (memoryId) => {
    if (!window.confirm('Supprimer cette mémoire ?')) return

    try {
      await client.delete(`/api/v1/memory/${memoryId}`)
      setMemories(prev => prev.filter(m => m.id !== memoryId))
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression')
      console.error(err)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    try {
      if (editingMemory) {
        const response = await client.put(`/api/v1/memory/${editingMemory.id}`, formData)
        setMemories(prev => prev.map(m => m.id === editingMemory.id ? response.data : m))
      } else {
        const response = await client.post('/api/v1/memory', formData)
        setMemories(prev => [response.data, ...prev])
      }
      setShowModal(false)
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la sauvegarde')
      console.error(err)
    }
  }

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A'
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp)
    return date.toLocaleString('fr-FR')
  }

  const getCategoryBadge = (category) => {
    const colors = {
      fact: 'primary',
      preference: 'success',
      instruction: 'warning'
    }
    return <Badge bg={colors[category] || 'secondary'}>{category}</Badge>
  }

  const categories = ['all', 'fact', 'preference', 'instruction']

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Mémoire Utilisateur</h2>
        <Button variant="primary" onClick={handleAdd}>
          <i className="bi bi-plus-circle"></i> Ajouter
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <ButtonGroup className="mb-0">
            {categories.map(cat => (
              <Button
                key={cat}
                variant={activeCategory === cat ? 'primary' : 'outline-primary'}
                onClick={() => setActiveCategory(cat)}
              >
                {cat === 'all' ? 'Tous' : cat}
                {cat !== 'all' && (
                  <Badge bg="light" text="dark" className="ms-2">
                    {memories.filter(m => m.category === cat).length}
                  </Badge>
                )}
              </Button>
            ))}
          </ButtonGroup>
        </Card.Body>
      </Card>

      {filteredMemories.length > 0 ? (
        <Card>
          <Card.Body className="p-0">
            <Table striped hover responsive className="mb-0">
              <thead>
                <tr>
                  <th>Clé</th>
                  <th>Valeur</th>
                  <th>Catégorie</th>
                  <th>Créé</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMemories.map(memory => (
                  <tr key={memory.id}>
                    <td><strong>{memory.key}</strong></td>
                    <td>{memory.value}</td>
                    <td>{getCategoryBadge(memory.category)}</td>
                    <td><small>{formatDate(memory.created_at)}</small></td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(memory)}
                      >
                        <i className="bi bi-pencil"></i>
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(memory.id)}
                      >
                        <i className="bi bi-trash"></i>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="info">
          Aucune mémoire dans la catégorie sélectionnée.
        </Alert>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editingMemory ? 'Modifier' : 'Ajouter'} une Mémoire</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Clé</Form.Label>
              <Form.Control
                type="text"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                placeholder="ex: favorite_color"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Valeur</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                placeholder="ex: blue"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Catégorie</Form.Label>
              <Form.Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="fact">Fait</option>
                <option value="preference">Préférence</option>
                <option value="instruction">Instruction</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Annuler
            </Button>
            <Button variant="primary" type="submit">
              {editingMemory ? 'Modifier' : 'Ajouter'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  )
}

export default Memory
